import NiceModal, { create, useModal } from '@ebay/nice-modal-react';
import Button from '@shared/components/Button/Button';
import Checkbox from '@shared/components/Checkbox/Checkbox';
import { DropdownSelectionProps } from '@shared/components/DropdownButton/DropdownSelection';
import Input from '@shared/components/InputGroup/Input';
import Popup from '@shared/components/Popup/Popup';
import Select from '@shared/components/Select/Select';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpirationPreset, formatExpiryDate, resolveExpiresAt, SCOPES } from 'api/meedioBackend/apiKeys/constants';
import { createApiKey } from 'api/meedioBackend/apiKeys/createApiKey';
import { ApiKeyScope } from 'api/meedioBackend/apiKeys/types';
import { ReactComponent as X } from 'assets/icons/X.svg';
import useScopedUserPowerLevels from 'hooks/useScopedUserPowerLevels/useScopedUserPowerLevels';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import testingConstants from 'utils/testingConstants';

import ApiKeyActionErrorModal from './ApiKeyActionErrorModal';
import ApiKeyCreatedModal from './ApiKeyCreatedModal';
import { API_KEYS_MODAL_WRAPPER_ID } from './constants';

const EXPIRATION_PRESETS: ExpirationPreset[] = ['week', 'month', 'quarter', 'half_year', 'no_expiration'];
const EXPIRATION_LABEL_KEYS: Record<ExpirationPreset, string> = {
  week: 'workspaces_settings.api_keys.create.expiration_week',
  month: 'workspaces_settings.api_keys.create.expiration_month',
  quarter: 'workspaces_settings.api_keys.create.expiration_quarter',
  half_year: 'workspaces_settings.api_keys.create.expiration_half_year',
  no_expiration: 'workspaces_settings.api_keys.create.expiration_none',
};

interface GenerateApiKeyModalProps {
  /** Backend workspace UUID the new key belongs to (already resolved by the caller). */
  workspaceUuid: string;
  /** Invoked after the user finishes the Created modal so the caller refreshes the Active list (AC-04-10). */
  onCreated?: () => void;
}

/**
 * Create modal (Figma 6392:66582): name + PL-gated scope checkboxes + expiration preset.
 * On 201 it hands the one-time secret to `ApiKeyCreatedModal`; on failure it opens the shared
 * action-error modal whose Try again re-submits this still-mounted form (AC-04-09).
 */
const GenerateApiKeyModal = create<GenerateApiKeyModalProps>(({ workspaceUuid, onCreated }) => {
  const { t } = useTranslation();
  const { remove } = useModal(GenerateApiKeyModal);
  const { workspaceId } = useWorkspacesContext({ requireWorkspaceId: true });
  const powerLevels = useScopedUserPowerLevels(workspaceId);

  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<Set<ApiKeyScope>>(new Set());
  const [preset, setPreset] = useState<ExpirationPreset>('no_expiration');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const expiresAtPreview = useMemo(() => {
    if (preset === 'no_expiration') return null;
    return formatExpiryDate(resolveExpiresAt(preset) as string);
  }, [preset]);

  const isValid = name.trim().length > 0 && selectedScopes.size > 0;

  const handleToggleScope = (scope: ApiKeyScope) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  };

  const handleSubmit = () => {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    createApiKey(workspaceUuid, {
      name: name.trim(),
      scopes: [...selectedScopes],
      expires_at: resolveExpiresAt(preset),
    })
      .then((response) => {
        remove();
        void NiceModal.show(ApiKeyCreatedModal, { apiKey: response.key, onFinish: onCreated });
      })
      .catch(() => {
        void NiceModal.show(ApiKeyActionErrorModal, { operation: 'create', onRetry: handleSubmit });
      })
      .finally(() => setIsSubmitting(false));
  };

  const expirationOptions: DropdownSelectionProps[] = EXPIRATION_PRESETS.map((value) => ({
    title: t(EXPIRATION_LABEL_KEYS[value]),
    onClick: () => setPreset(value),
    isSelected: preset === value,
  }));

  return (
    <Popup isVisible closePopup={remove} isViewportScrollable wrapperId={API_KEYS_MODAL_WRAPPER_ID}>
      <Popup.Container
        className="shadow-modal !max-w-[calc(100vw-2rem)] md:w-[573px] md:!max-w-[573px]"
        dataTestId={testingConstants.generateApiKeyModal}
      >
        <div className="flex shrink-0 items-center gap-4 px-5 pb-4 pt-5">
          <span aria-hidden className="h-8 w-8 shrink-0 md:hidden" />
          <span className="text-grayscale-black text-size-md flex-1 text-center font-semibold md:text-left">
            {t('workspaces_settings.api_keys.create.title')}
          </span>
          <button
            type="button"
            onClick={remove}
            aria-label={t('close')}
            className="hover:bg-gray-30 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl p-2.5"
          >
            <X className="stroke-grayscale-black h-5 w-5 stroke-2" />
          </button>
        </div>

        <Popup.Divider />

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain p-5">
          <div className="flex flex-col gap-2">
            <span className="text-grayscale-black text-size-sm font-medium">
              {t('workspaces_settings.api_keys.create.name_label')}
            </span>
            <Input
              inputSize="sm"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('workspaces_settings.api_keys.create.name_placeholder')}
              required={false}
              data-testid={testingConstants.generateApiKeyNameInput}
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-grayscale-black text-size-sm font-medium">
              {t('workspaces_settings.api_keys.create.scope_label')}
            </span>
            <div className="grid grid-cols-1 gap-x-4 gap-y-2 md:grid-flow-col md:grid-cols-2 md:grid-rows-3">
              {SCOPES.map(({ scope, labelKey, minPowerLevel, dimension }) => {
                const isDisabled = powerLevels[dimension] < minPowerLevel;
                return (
                  <Checkbox
                    key={scope}
                    size="sm"
                    checked={selectedScopes.has(scope)}
                    disabled={isDisabled}
                    onChange={() => handleToggleScope(scope)}
                    data-testid={`${testingConstants.generateApiKeyScopeCheckbox}-${scope}`}
                  >
                    {t(labelKey)}
                  </Checkbox>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-grayscale-black text-size-sm font-medium">
              {t('workspaces_settings.api_keys.create.expiration_label')}
            </span>
            <Select size="sm" options={expirationOptions} dataTestId={testingConstants.generateApiKeyExpirationSelect}>
              {t(EXPIRATION_LABEL_KEYS[preset])}
            </Select>
            {expiresAtPreview && (
              <span className="text-gray-80 text-size-xs">
                {t('workspaces_settings.api_keys.create.expires_on', { date: expiresAtPreview })}
              </span>
            )}
          </div>
        </div>

        <Popup.Divider />

        <div className="flex shrink-0 justify-end gap-2 px-5 pb-5 pt-4">
          <Button
            variant="secondaryTertiary"
            size="sm"
            onClick={remove}
            data-testid={testingConstants.generateApiKeyCancelButton}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!isValid}
            loading={isSubmitting}
            data-testid={testingConstants.generateApiKeySubmitButton}
          >
            {t('workspaces_settings.api_keys.create.submit')}
          </Button>
        </div>
      </Popup.Container>
    </Popup>
  );
});

export default GenerateApiKeyModal;
